import { apiClient } from './client';

/**
 * Per-session metric scorecard (EEG + IMU vs faculty/mastery).
 *   GET /api/v1/tenants/{id}/sessions/{sessionId}/metric-comparison
 *
 * Drives the card between the Activity timeline and the cognitive trace on
 * the participant drill-down: each metric's task-phase value alongside the
 * faculty reference from master_baselines + a better/worse marker.
 */

export interface MetricReference {
  cohort: string; // 'not_practice_ready' | 'practice_ready' | 'attending' | …
  tier: number; // 0 novice · 1 practice-ready · 2 expert
  value: number;
  statistic: string;
  sample_size: number | null;
  source: string | null;
  /** Whether the source study found this cohort difference statistically
   *  significant. Non-significant references are shown as de-emphasised
   *  context but never drive the verdict. Defaults true on older payloads. */
  significant?: boolean;
}

/** Tukey box-plot summary of a metric across ALL the participant's task-phase
 *  sessions of this procedure — median, middle-50% box, whiskers (to the most
 *  extreme value inside the 1.5·IQR fences), and outliers beyond them. Null on
 *  a row when the participant has too few sessions for a stable summary. All
 *  values are on the metric's own scale, same as `session_value`. */
export interface MetricDistribution {
  n: number;
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
  whisker_low: number;
  whisker_high: number;
  outliers: number[];
}

export interface MetricComparisonRow {
  metric: string;
  modality: string; // 'eeg' | 'hand' | 'both'
  unit: string | null;
  direction: string; // 'higher' | 'lower' | 'none'
  session_value: number | null;
  references: MetricReference[]; // novice → expert
  beats_reference: boolean | null; // vs the expert tier
  /** Mean of this metric over the participant's recent prior sessions of the
   *  same procedure (task phase), for a session-over-session trend. Null when
   *  there's no prior history. Count is `recent_session_count` below. */
  recent_mean: number | null;
  /** Box-plot summary of the participant's own range on this procedure — "this
   *  session vs your range". Null when they have too few sessions. */
  distribution?: MetricDistribution | null;
}

/** Cached LLM coaching narration for a session (ROADMAP AI-COACH phase 3c),
 *  served when the worker has generated one and coaching is enabled. Null on
 *  the comparison → the AI Coach card uses its deterministic derived copy. */
export interface SessionCoaching {
  headline: string;
  bullets: string[];
  next_goal: string | null;
  model_id: string;
}

/** One of the coach's points from the session's coaching debrief
 *  (AI-COACH-FROM-COACHING-TRANSCRIPT): the advice, the coach's own words
 *  (verbatim from the transcript), where in which debrief, and what the
 *  session's metrics show about it. */
export interface DebriefPoint {
  point: string;
  quote: string;
  /** The coaching attachment (debrief) the quote is from. */
  attachment_id: string;
  /** Seconds into that debrief. */
  start_s: number;
  evidence: string;
  support: 'supported' | 'differs' | 'no_signal';
}

export interface SessionMetricComparison {
  session_id: string;
  procedure_name: string | null;
  procedure_group: string | null;
  rows: MetricComparisonRow[];
  /** How many recent prior sessions the `recent_mean` values average over. */
  recent_session_count: number;
  /** LLM coaching narration when generated + enabled; null → derived copy. */
  coaching: SessionCoaching | null;
  /** The coach's points from the debrief — only when the tenant lets the AI
   *  Coach read debriefs and one has been processed; null/absent otherwise. */
  debrief?: DebriefPoint[] | null;
}

export async function getSessionMetricComparison(
  tenantId: string,
  sessionId: string,
): Promise<SessionMetricComparison> {
  const { data } = await apiClient.get<SessionMetricComparison>(
    `/api/v1/tenants/${tenantId}/sessions/${encodeURIComponent(
      sessionId,
    )}/metric-comparison`,
  );
  return data;
}

/** Participant-facing variant — the caller has no tenant id; the server
 *  resolves which of the participant's tenants owns the session. */
export async function getMySessionMetricComparison(
  sessionId: string,
): Promise<SessionMetricComparison> {
  const { data } = await apiClient.get<SessionMetricComparison>(
    `/api/v1/sessions/me/${encodeURIComponent(sessionId)}/metric-comparison`,
  );
  return data;
}

/** Per-metric reference cohorts for a PROCEDURE (not a session) — the same
 *  master_baselines rows the scorecard overlays, keyed by master metric key.
 *  Drives the Compare view's not-practice-ready / practice-ready ticks. */
export async function getProcedureMetricReferences(
  tenantId: string,
  procedureId: string,
): Promise<Record<string, MetricReference[]>> {
  const { data } = await apiClient.get<Record<string, MetricReference[]>>(
    `/api/v1/tenants/${tenantId}/procedures/${procedureId}/metric-references`,
  );
  return data;
}

/** Participant Compare scaffold (COMPARE-CONFIG) — visible metrics + the
 *  selected procedure's reference cohorts + whether the participant has any
 *  sessions (drives the "We are ready for your data" empty state). */
export interface CompareMetricInfo {
  key: string;
  modality: string; // 'eeg' | 'hand' | 'both'
  direction: string; // 'higher' | 'lower' | 'none'
  unit: string | null;
}
export interface ProcedureRef {
  id: string;
  name: string;
}
export interface ParticipantCompareScaffold {
  tenant_id: string;
  compare_enabled: boolean;
  has_any_sessions: boolean;
  procedures: ProcedureRef[];
  metrics: CompareMetricInfo[];
  references: Record<string, MetricReference[]>;
}

/** Self-vs-cohort data for the participant Compare view (COMPARE-CONFIG). */
export interface ParticipantCompareData {
  procedure_id: string;
  you: Record<string, number>;
  you_sessions: number;
  other: Record<string, number>;
  other_label: string;
  other_participants: number;
  other_below_threshold: boolean;
  min_participants: number;
  references: Record<string, MetricReference[]>;
}

export async function getMyCompareData(opts: {
  procedureId: string;
  against: string;
  tenantId?: string;
}): Promise<ParticipantCompareData> {
  const { data } = await apiClient.get<ParticipantCompareData>(
    '/api/v1/sessions/me/compare-data',
    {
      params: {
        procedure_id: opts.procedureId,
        against: opts.against,
        tenant_id: opts.tenantId,
      },
    },
  );
  return data;
}

export async function getMyCompareScaffold(opts?: {
  tenantId?: string;
  procedureId?: string;
}): Promise<ParticipantCompareScaffold> {
  const { data } = await apiClient.get<ParticipantCompareScaffold>(
    '/api/v1/sessions/me/compare-scaffold',
    {
      params: {
        tenant_id: opts?.tenantId,
        procedure_id: opts?.procedureId,
      },
    },
  );
  return data;
}

/** One toggleable scorecard metric — its key + section (modality).
 *  Labels are a display concern the portal owns (see util/metricLabels). */
export interface MetricCatalogueItem {
  key: string;
  modality: string; // 'eeg' | 'hand' | 'both'
  /** An EEG index that exists only in the cognitive trace (Stress, Fatigue):
   *  hiding it hides the trace series; there is no scorecard row. */
  trace_only?: boolean;
}

/** The full set of scorecard metrics in registry order — drives the
 *  tenant Settings → Metrics visibility checkboxes. */
export async function getMetricCatalogue(): Promise<MetricCatalogueItem[]> {
  const { data } = await apiClient.get<MetricCatalogueItem[]>(
    '/api/v1/metric-catalogue',
  );
  return data;
}

/** The metric keys Settings → Metrics hides for one session (its
 *  procedure's override, else the tenant default). The cognitive trace
 *  leaves these series out. `tenantId` null → the participant's own
 *  session via /me. */
export async function getSessionHiddenMetrics(
  tenantId: string | null,
  sessionId: string,
): Promise<string[]> {
  const sid = encodeURIComponent(sessionId);
  const { data } = await apiClient.get<{ hidden_metrics: string[] }>(
    tenantId
      ? `/api/v1/tenants/${tenantId}/sessions/${sid}/hidden-metrics`
      : `/api/v1/sessions/me/${sid}/hidden-metrics`,
  );
  return data.hidden_metrics;
}

/** A procedure's scorecard metric-visibility override state (see the
 *  per-procedure endpoints on the procedures router). `has_override` false
 *  means the procedure inherits the tenant-wide hidden set. */
export interface ProcedureHiddenMetrics {
  procedure_id: string;
  has_override: boolean;
  hidden_metrics: string[];
}

export async function getProcedureHiddenMetrics(
  tenantId: string,
  procedureId: string,
): Promise<ProcedureHiddenMetrics> {
  const { data } = await apiClient.get<ProcedureHiddenMetrics>(
    `/api/v1/tenants/${tenantId}/procedures/${procedureId}/hidden-metrics`,
  );
  return data;
}

/** Set (override) a procedure's hidden set — full replace. */
export async function setProcedureHiddenMetrics(
  tenantId: string,
  procedureId: string,
  hiddenMetrics: string[],
): Promise<ProcedureHiddenMetrics> {
  const { data } = await apiClient.put<ProcedureHiddenMetrics>(
    `/api/v1/tenants/${tenantId}/procedures/${procedureId}/hidden-metrics`,
    { hidden_metrics: hiddenMetrics },
  );
  return data;
}

/** Remove a procedure's override so it reverts to the tenant default. */
export async function clearProcedureHiddenMetrics(
  tenantId: string,
  procedureId: string,
): Promise<void> {
  await apiClient.delete(
    `/api/v1/tenants/${tenantId}/procedures/${procedureId}/hidden-metrics`,
  );
}

// --- Post-recording self-assessment (CAPTURE-SESSION-SURVEY) ----------

/** How much guidance the participant received (Zwisch, least → most autonomy). */
export type SurveyGuidance =
  | 'show_and_tell'
  | 'active_help'
  | 'passive_help'
  | 'supervision_only';
export type SurveyPerformance =
  | 'unprepared'
  | 'inexperienced'
  | 'intermediate'
  | 'practice_ready'
  | 'exceptional';
export type SurveyComplexity = 'easiest_third' | 'average' | 'hardest_third';

export interface SessionSurvey {
  session_id: string;
  /** Which instrument the answers belong to, e.g. `simpl_self_v1`. */
  instrument: string;
  /** Who answered — `participant` today. */
  respondent: string;
  /** True when they were asked and said no. Not the same as no survey at all. */
  declined: boolean;
  guidance: SurveyGuidance | null;
  performance: SurveyPerformance | null;
  complexity: SurveyComplexity | null;
  created_at: string;
  updated_at: string | null;
}

/**
 * The participant's own rating of a recording, or null when they were never
 * prompted (an older capture-app build, or a session that predates the
 * survey). A DECLINE comes back as a row with `declined: true` — a different
 * fact, and worth showing as one.
 */
export async function getSessionSurvey(
  tenantId: string,
  sessionId: string,
): Promise<SessionSurvey | null> {
  const { data } = await apiClient.get<SessionSurvey | null>(
    `/api/v1/tenants/${tenantId}/sessions/${encodeURIComponent(
      sessionId,
    )}/survey`,
  );
  return data ?? null;
}

/**
 * The same, for the participant's OWN session — the dashboard drill-down
 * has no tenant id, so it asks by session alone and the server resolves
 * which of their tenants owns it (a session that isn't theirs 404s).
 */
export async function getMySessionSurvey(
  sessionId: string,
): Promise<SessionSurvey | null> {
  const { data } = await apiClient.get<SessionSurvey | null>(
    `/api/v1/sessions/me/${encodeURIComponent(sessionId)}/survey`,
  );
  return data ?? null;
}
